import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CHATBOT_INTENTS } from '../chatbot-intents.config';
import { ChatAction } from '../chatbot.types';

/** Human-readable labels for intent IDs (Spanish UI text kept as data); falls back to an auto-generated label. */
export const INTENT_LABELS: Readonly<Record<string, string>> = {
  'greeting':                                   'Saludo / Bienvenida',
  'help':                                       'Ayuda / Instrucciones',
  'context-agrupation':                         'Cambiar agrupación',
  'context-entity':                             'Cambiar entidad',
  'context-client':                             'Cambiar cliente',
  'detect-date':                                'Detectar rango de fechas',
  'query-fugas':                                'Detección de fugas',
  'query-parados':                              'Contadores sin comunicación',
  'query-consumo-negativo':                     'Consumo negativo',
  'query-picos-anomalos':                       'Picos de consumo anómalos',
  'query-comparacion':                          'Comparar consumos entre períodos',
  'query-valores-iniciales':                    'Valores iniciales tras instalación',
  'query-consumo-hora':                         'Contadores por consumo horario',
  'query-ultimo-valor':                         'Contadores sin valor reciente',
  'navigate-home':                              'Panel principal',
  'navigate-home-entidades':                    'Panel – vista entidades',
  'navigate-home-global':                       'Panel – vista global',
  'navigate-dispositivos':                      'Dispositivos en red',
  'navigate-dispositivos-no-asignados':         'Dispositivos – no asignados',
  'navigate-dispositivos-desactivados':         'Dispositivos – desactivados',
  'navigate-dispositivos-cliente':              'Dispositivos – del cliente',
  'navigate-dispositivos-revision':             'Dispositivos – en revisión',
  'navigate-dispositivos-sustituciones-pendientes': 'Dispositivos – sustituciones pendientes',
  'navigate-dispositivos-sustituciones':        'Dispositivos – historial de sustituciones',
  'navigate-dispositivos-sin-comunicacion':     'Dispositivos – sin comunicación',
  'navigate-dispositivos-error-activacion':     'Dispositivos – error de activación',
  'navigate-dispositivos-bateria-baja':         'Dispositivos – batería baja',
  'navigate-dispositivos-interno':              'Dispositivos – red interna LoRa',
  'navigate-dispositivos-gprs':                 'Dispositivos – GPRS interno',
  'navigate-dispositivos-walk-by':              'Dispositivos – walk-by',
  'navigate-dispositivos-mbus':                 'Dispositivos – M-Bus desconocidos',
  'navigate-dispositivos-ignorados':            'Dispositivos – ignorados',
  'navigate-dispositivos-nuevo':                'Añadir nuevo dispositivo',
  'navigate-dispositivos-sim':                  'Gestión de tarjetas SIM',
  'navigate-dispositivos-importar-lecturas':    'Importar lecturas',
  'navigate-dispositivos-importar-texto':       'Importar fichero de texto',
  'navigate-dispositivos-importar-imagenes':    'Importar imágenes de dispositivos',
  'navigate-dispositivos-importar-datos':       'Importar datos de dispositivos',
  'navigate-dispositivos-mbus-clave-primaria':  'M-Bus – clave primaria',
  'navigate-dispositivos-mbus-clave-comun':     'M-Bus – clave común',
  'navigate-valvulas':                          'Válvulas – listado',
  'navigate-valvulas-cerradas':                 'Válvulas – cerradas',
  'navigate-valvulas-abiertas':                 'Válvulas – abiertas',
  'navigate-valvulas-pendiente-cierre':         'Válvulas – cierre pendiente',
  'navigate-valvulas-pendiente-apertura':       'Válvulas – apertura pendiente',
  'navigate-gateways':                          'Gateways (concentradores)',
  'navigate-gateways-disponibles':              'Gateways – disponibles',
  'navigate-gateways-en-stock':                 'Gateways – en stock',
  'navigate-gateways-averiados':                'Gateways – averiados',
  'navigate-gateways-reparacion':               'Gateways – en reparación',
  'navigate-gateways-appeui':                   'Gateways – AppEUI',
  'navigate-alarmas':                           'Alarmas / Alertas',
  'navigate-alarmas-config':                    'Alarmas – configuración',
  'navigate-alarmas-entidad-config':            'Alarmas – configuración de entidad',
  'navigate-alarmas-global':                    'Alarmas – globales',
  'navigate-alarmas-personalizadas':            'Alarmas – personalizadas',
  'navigate-alarmas-dispositivos':              'Alarmas – por dispositivo',
  'navigate-clientes':                          'Listado de clientes',
  'navigate-usuarios':                          'Usuarios',
  'navigate-usuarios-log':                      'Usuarios – log global',
  'navigate-usuarios-log-agrupacion':           'Usuarios – log por agrupación',
  'navigate-usuarios-importar':                 'Importar usuarios',
  'navigate-analisis':                          'Análisis / Gráficas',
  'navigate-analisis-balance':                  'Análisis – balance de consumo',
  'navigate-analisis-evolucion':                'Análisis – evolución de consumo',
  'navigate-analisis-comparativa':              'Análisis – gráficas comparativas',
  'navigate-analisis-consultas':                'Análisis – consultas de datos',
  'navigate-analisis-avanzada':                 'Análisis – analítica avanzada',
  'navigate-analisis-asociaciones':             'Análisis – asociaciones',
  'navigate-cobertura':                         'Cobertura / Señal de red',
  'navigate-cobertura-mbus':                    'Cobertura – mapa M-Bus',
  'navigate-mapa':                              'Mapa de dispositivos',
  'navigate-mantenimiento':                     'Mantenimiento',
  'navigate-mantenimiento-busqueda':            'Mantenimiento – búsqueda global',
  'navigate-mantenimiento-activaciones':        'Mantenimiento – activaciones',
  'navigate-mantenimiento-estado-comunicaciones': 'Mantenimiento – estado de comunicaciones',
  'navigate-mantenimiento-alarmas-globales':    'Mantenimiento – alarmas globales',
  'navigate-mantenimiento-concentradores':      'Mantenimiento – concentradores',
  'navigate-mantenimiento-bateria':             'Mantenimiento – estado de batería',
  'navigate-mantenimiento-cups-multiples':      'Mantenimiento – CUPS con múltiples dispositivos',
  'navigate-mantenimiento-incidencias':         'Mantenimiento – incidencias',
  'navigate-mantenimiento-cola-comandos':       'Mantenimiento – cola de comandos',
  'navigate-mantenimiento-usuarios-globales':   'Mantenimiento – usuarios globales',
  'navigate-mantenimiento-usuarios-cliente':    'Mantenimiento – usuarios del cliente',
  'navigate-mantenimiento-eventos-usuario':     'Mantenimiento – eventos de usuario',
  'navigate-mantenimiento-reporte-comunicaciones': 'Mantenimiento – reporte de comunicaciones',
  'navigate-mantenimiento-lns':                 'Mantenimiento – servidor LNS',
  'navigate-mantenimiento-tareas':              'Mantenimiento – tareas de gateways',
  'navigate-mantenimiento-registros':           'Mantenimiento – registros/logs',
  'navigate-mantenimiento-construccion-red':    'Mantenimiento – construcción de red',
  'navigate-mantenimiento-entidad-gateways-cliente': 'Mantenimiento – gateways del cliente',
  'navigate-mantenimiento-entidad-gateways':    'Mantenimiento – gateways de entidad',
  'navigate-mantenimiento-entidad-concentradores': 'Mantenimiento – concentradores de entidad',
  'navigate-mantenimiento-editar-agrupaciones': 'Mantenimiento – editar agrupaciones',
  'navigate-mantenimiento-optimizar-red':       'Mantenimiento – optimizar red',
  'navigate-mantenimiento-borrado-datos':       'Mantenimiento – borrado de datos',
  'navigate-mantenimiento-monitorizacion':      'Mantenimiento – monitorización',
  'navigate-mantenimiento-fabricantes':         'Mantenimiento – fabricantes',
  'navigate-mantenimiento-cron':                'Mantenimiento – tareas programadas (cron)',
  'navigate-mantenimiento-ip':                  'Mantenimiento – configuración IP',
  'navigate-mantenimiento-busqueda-por-modelo': 'Mantenimiento – búsqueda por modelo',
  'navigate-telecontrol':                       'Telecontrol / Control remoto',
  'navigate-telecontrol-historico':             'Telecontrol – histórico',
  'navigate-telecontrol-esquema':               'Telecontrol – esquema de red',
  'navigate-telecontrol-telemedida':            'Telecontrol – telemedida',
  'navigate-telecontrol-dataloggers':           'Telecontrol – dataloggers',
  'navigate-telecontrol-test':                  'Telecontrol – prueba',
  'navigate-ficheros':                          'Ficheros / Archivos de salida',
  'navigate-ficheros-plantillas':               'Ficheros – plantillas',
  'navigate-ficheros-horarios':                 'Ficheros – horarios',
  'navigate-ficheros-ek280':                    'Ficheros – EK280',
  'navigate-ficheros-aqualia':                  'Ficheros – Aqualia',
  'navigate-gestion-datos':                     'Gestión de datos',
  'navigate-gestion-datos-cups-instalados':     'Gestión datos – CUPS instalados',
  'navigate-gestion-datos-sin-cups':            'Gestión datos – sin CUPS',
  'navigate-gestion-datos-configuracion':       'Gestión datos – configuración',
  'navigate-gestion-datos-metrologia':          'Gestión datos – metrología',
  'navigate-gestion-datos-importacion':         'Gestión datos – importación',
  'navigate-gestion-datos-pta':                 'Gestión datos – PTA',
  'navigate-estado-red':                        'Estado de red',
  'navigate-estado-red-entidad':                'Estado red – entidad',
  'navigate-estado-red-kpis':                   'Estado red – KPIs',
  'navigate-estado-red-global':                 'Estado red – global',
  'navigate-estado-red-resumen-global':         'Estado red – resumen global',
  'navigate-entidades':                         'Listado de entidades',
  'navigate-cups':                              'CUPS / Puntos de suministro',
  'navigate-agrupaciones':                      'Listado de agrupaciones',
  'navigate-sensorica':                         'Sensorica / Sensores',
  'navigate-sensorica-presion':                 'Sensorica – presión',
  'navigate-sensorica-caudal':                  'Sensorica – caudal',
  'navigate-ordenes':                           'Órdenes de trabajo',
  'navigate-ordenes-rutas':                     'Órdenes – rutas de trabajo',
  'navigate-instalador':                        'Instalador / Activación',
  'navigate-instalador-historico':              'Instalador – histórico',
  'navigate-abonado':                           'Panel abonado (consumidor final)',
  'navigate-abonado-configuracion':             'Abonado – configuración',
};

/** Example utterances shown as guide chips, keyed by intent ID. */
export const INTENT_EXAMPLES: Readonly<Record<string, string[]>> = {
  // ── Conversational ───────────────────────────────────────────────────────────
  'greeting':                   ['Hola', 'Buenos días'],
  'help':                       ['Ayuda', '¿Qué puedes hacer?'],
  // ── Context switches ─────────────────────────────────────────────────────────
  'context-agrupation':         ['Agrupación Norte', 'Ir a zona Central'],
  'context-entity':             ['Entidad Caltaqua', 'Cambiar a entidad Aqualia'],
  'context-client':             ['Cliente Suez', 'Ver cliente Hidralia'],
  'detect-date':                ['Últimos 7 días', 'Enero 2024'],
  // ── Query filters ────────────────────────────────────────────────────────────
  'query-fugas':                ['Contadores con fugas en Caltaqua', 'Fugas mayores de 15 litros hoy'],
  'query-parados':              ['Contadores parados este mes', 'Contadores que se han parado el último mes'],
  'query-consumo-negativo':     ['Consumo negativo última semana', 'Contadores consumo negativo'],
  'query-picos-anomalos':       ['Picos anómalos ayer', '¿Hay contadores con picos?'],
  'query-comparacion':          ['Comparar consumo enero vs febrero', 'Comparativa dos períodos'],
  'query-valores-iniciales':    ['Contadores instalados este mes', '¿Cuáles tienen valor inicial?'],
  'query-consumo-hora':         ['Consumo por hora mayor de 10 L', 'Contadores por consumo horario'],
  'query-ultimo-valor':         ['Contadores sin lecturas recientes', 'Sin valor en los últimos 4 días'],
  // ── Navigation: Home ─────────────────────────────────────────────────────────
  'navigate-home':              ['Inicio', 'Panel principal'],
  'navigate-home-entidades':    ['Panel entidades', 'Dashboard entidades'],
  'navigate-home-global':       ['Vista global', 'Panel global'],
  // ── Navigation: Devices ─────────────────────────────────────────────────
  'navigate-dispositivos':                          ['Dispositivos', 'Ver contadores'],
  'navigate-dispositivos-no-asignados':             ['Sin asignar', 'Dispositivos no asignados'],
  'navigate-dispositivos-desactivados':             ['Desactivados', 'Medidores desactivados'],
  'navigate-dispositivos-cliente':                  ['Dispositivos del cliente', 'Contadores del cliente'],
  'navigate-dispositivos-revision':                 ['En revisión', 'Dispositivos en revisión'],
  'navigate-dispositivos-sustituciones-pendientes': ['Sustituciones pendientes', '¿Qué cambios están pendientes?'],
  'navigate-dispositivos-sustituciones':            ['Historial de sustituciones', 'Dispositivos sustituidos'],
  'navigate-dispositivos-sin-comunicacion':         ['Sin comunicación', 'Dispositivos offline'],
  'navigate-dispositivos-error-activacion':         ['Error de activación', 'Fallo activación'],
  'navigate-dispositivos-bateria-baja':             ['Batería baja', 'Dispositivos con poca batería'],
  'navigate-dispositivos-interno':                  ['Red interna LoRa', 'Dispositivos internos'],
  'navigate-dispositivos-gprs':                     ['GPRS internos', 'Ver dispositivos GPRS'],
  'navigate-dispositivos-walk-by':                  ['Walk-by', 'Lectura en campo'],
  'navigate-dispositivos-mbus':                     ['M-Bus desconocidos', 'Dispositivos M-Bus sin asignar'],
  'navigate-dispositivos-ignorados':                ['Ignorados', '¿Qué dispositivos están ignorados?'],
  'navigate-dispositivos-nuevo':                    ['Nuevo dispositivo', 'Añadir dispositivo'],
  'navigate-dispositivos-sim':                      ['Tarjetas SIM', 'Gestión SIM'],
  'navigate-dispositivos-importar-lecturas':        ['Importar lecturas', 'Subir lecturas'],
  'navigate-dispositivos-importar-texto':           ['Importar fichero texto', 'Cargar fichero de texto'],
  'navigate-dispositivos-importar-imagenes':        ['Importar imágenes', 'Subir imágenes de dispositivos'],
  'navigate-dispositivos-importar-datos':           ['Importar datos dispositivos', 'Cargar datos de dispositivos'],
  'navigate-dispositivos-mbus-clave-primaria':      ['Clave primaria M-Bus', 'M-Bus clave primaria'],
  'navigate-dispositivos-mbus-clave-comun':         ['Clave común M-Bus', 'M-Bus clave común'],
  'navigate-valvulas':                              ['Listado de válvulas', 'Contadores con válvula'],
  'navigate-valvulas-cerradas':                     ['Válvulas cerradas', 'Listado de válvulas cerradas en Caltaqua'],
  'navigate-valvulas-abiertas':                     ['Válvulas abiertas', 'Open valves'],
  'navigate-valvulas-pendiente-cierre':             ['Válvulas pendientes de cierre', 'Valves pending closing'],
  'navigate-valvulas-pendiente-apertura':           ['Válvulas pendientes de apertura', 'Valves pending opening'],
  // ── Navigation: Gateways ─────────────────────────────────────────────────────
  'navigate-gateways':              ['Gateways', 'Ver concentradores'],
  'navigate-gateways-disponibles':  ['Gateways disponibles', 'Concentradores disponibles'],
  'navigate-gateways-en-stock':     ['Gateways en stock', 'Concentradores en stock'],
  'navigate-gateways-averiados':    ['Gateways averiados', '¿Cuáles están rotos?'],
  'navigate-gateways-reparacion':   ['Gateways en reparación', 'Concentradores reparándose'],
  'navigate-gateways-appeui':       ['AppEUI', 'Configuración AppEUI'],
  // ── Navigation: Alarms ───────────────────────────────────────────────────────
  'navigate-alarmas':               ['Alarmas', 'Ver alarmas activas'],
  'navigate-alarmas-config':        ['Configurar alarmas', 'Configuración de alarmas'],
  'navigate-alarmas-entidad-config':['Alarmas entidad', 'Configuración alarmas de entidad'],
  'navigate-alarmas-global':        ['Alarmas globales', 'Todas las alarmas del sistema'],
  'navigate-alarmas-personalizadas':['Alarmas personalizadas', 'Mis alertas custom'],
  'navigate-alarmas-dispositivos':  ['Alarmas de dispositivos', 'Alertas por contador'],
  // ── Navigation: Clients ───────────────────────────────────────────────────────
  'navigate-clientes':              ['Clientes', 'Listado de clientes'],
  // ── Navigation: Users ─────────────────────────────────────────────────────
  'navigate-usuarios':              ['Usuarios', 'Gestionar usuarios'],
  'navigate-usuarios-log':          ['Log de usuarios', '¿Quién ha accedido?'],
  'navigate-usuarios-log-agrupacion':['Log usuarios agrupación', 'Historial de accesos por zona'],
  'navigate-usuarios-importar':     ['Importar usuarios', 'Cargar lista de usuarios'],
  // ── Navigation: Analysis ─────────────────────────────────────────────────────
  'navigate-analisis':              ['Análisis', 'Ver gráficas de consumo'],
  'navigate-analisis-balance':      ['Balance de consumo', 'Balance energético último mes'],
  'navigate-analisis-evolucion':    ['Evolución consumo', 'Tendencia de consumo anual'],
  'navigate-analisis-comparativa':  ['Gráficas comparativas', 'Comparar gráficas de dos zonas'],
  'navigate-analisis-consultas':    ['Consultas de datos', 'Consultar datos por filtro'],
  'navigate-analisis-avanzada':     ['Analítica avanzada', 'Análisis avanzado'],
  'navigate-analisis-asociaciones': ['Asociaciones', 'Análisis de asociaciones'],
  // ── Navigation: Coverage ─────────────────────────────────────────────────────
  'navigate-cobertura':             ['Cobertura', 'Ver señal de red LoRa'],
  'navigate-cobertura-mbus':        ['Cobertura M-Bus', 'Mapa señal M-Bus'],
  'navigate-mapa':                  ['Mapa', 'Ver mapa de dispositivos'],
  // ── Navigation: Maintenance ──────────────────────────────────────────────────
  'navigate-mantenimiento':                         ['Mantenimiento', 'Estado del sistema'],
  'navigate-mantenimiento-busqueda':                ['Búsqueda global mantenimiento', 'Buscar en mantenimiento'],
  'navigate-mantenimiento-activaciones':            ['Activaciones mantenimiento', 'Ver activaciones'],
  'navigate-mantenimiento-estado-comunicaciones':   ['Estado comunicaciones', '¿Cómo está la comunicación?'],
  'navigate-mantenimiento-alarmas-globales':        ['Alarmas globales mantenimiento', 'Mantenimiento alarmas'],
  'navigate-mantenimiento-concentradores':          ['Concentradores mantenimiento', 'Mantenimiento concentradores'],
  'navigate-mantenimiento-bateria':                 ['Estado batería global', 'Batería mantenimiento'],
  'navigate-mantenimiento-cups-multiples':          ['CUPS múltiples', 'Contadores con varios CUPS'],
  'navigate-mantenimiento-incidencias':             ['Incidencias de mantenimiento', 'Reporte de incidencias'],
  'navigate-mantenimiento-cola-comandos':           ['Cola de comandos', 'Comandos pendientes'],
  'navigate-mantenimiento-usuarios-globales':       ['Usuarios globales', 'Mantenimiento usuarios globales'],
  'navigate-mantenimiento-usuarios-cliente':        ['Usuarios del cliente', 'Mantenimiento usuarios cliente'],
  'navigate-mantenimiento-eventos-usuario':         ['Eventos de usuario', 'Log de eventos'],
  'navigate-mantenimiento-reporte-comunicaciones':  ['Reporte comunicaciones', 'Informe de comunicaciones'],
  'navigate-mantenimiento-lns':                     ['LNS', 'Servidor de red LNS'],
  'navigate-mantenimiento-tareas':                  ['Tareas gateways', 'Programación gateways'],
  'navigate-mantenimiento-registros':               ['Registros mantenimiento', 'Logs del sistema'],
  'navigate-mantenimiento-construccion-red':        ['Construcción de red', 'Planificación de red'],
  'navigate-mantenimiento-entidad-gateways-cliente':['Gateways cliente entidad', 'Gateways del cliente'],
  'navigate-mantenimiento-entidad-gateways':        ['Gateways entidad', 'Mantenimiento gateways entidad'],
  'navigate-mantenimiento-entidad-concentradores':  ['Concentradores entidad', 'Mantenimiento concentradores entidad'],
  'navigate-mantenimiento-editar-agrupaciones':     ['Editar agrupaciones', 'Gestionar agrupaciones entidad'],
  'navigate-mantenimiento-optimizar-red':           ['Optimizar red', '¿Cómo mejorar la red?'],
  'navigate-mantenimiento-borrado-datos':           ['Borrado de datos', 'Eliminar datos'],
  'navigate-mantenimiento-monitorizacion':          ['Monitorización', 'Fiscalizador'],
  'navigate-mantenimiento-fabricantes':             ['Fabricantes', 'Configurar fabricantes'],
  'navigate-mantenimiento-cron':                    ['Cron', 'Tareas programadas'],
  'navigate-mantenimiento-ip':                      ['Configuración IP', 'IP mantenimiento'],
  'navigate-mantenimiento-busqueda-por-modelo':     ['¿Cuántos contadores Diehl hay?', 'Búsqueda por modelo'],
  // ── Navigation: Remote control ───────────────────────────────────────────────
  'navigate-telecontrol':           ['Telecontrol', 'Control remoto válvulas'],
  'navigate-telecontrol-historico': ['Histórico telecontrol', 'Historial acciones remotas'],
  'navigate-telecontrol-esquema':   ['Esquema de red telecontrol', 'Topología telecontrol'],
  'navigate-telecontrol-telemedida':['Telemedida', 'Telemetría remota'],
  'navigate-telecontrol-dataloggers':['Dataloggers', 'Registradores de datos'],
  'navigate-telecontrol-test':      ['Prueba telecontrol', 'Test de telecontrol'],
  // ── Navigation: Files ────────────────────────────────────────────────────────
  'navigate-ficheros':              ['Ficheros', 'Archivos de salida'],
  'navigate-ficheros-plantillas':   ['Plantillas de ficheros', 'Editar plantillas'],
  'navigate-ficheros-horarios':     ['Horarios de ficheros', 'Ficheros programados'],
  'navigate-ficheros-ek280':        ['Ficheros EK280', 'EK280'],
  'navigate-ficheros-aqualia':      ['Ficheros Aqualia', 'Aqualia files'],
  // ── Navigation: Data management ─────────────────────────────────────────────
  'navigate-gestion-datos':                     ['Gestión de datos', 'Importar / Exportar'],
  'navigate-gestion-datos-cups-instalados':     ['CUPS instalados', 'Listado CUPS instalados'],
  'navigate-gestion-datos-sin-cups':            ['Sin CUPS', 'Dispositivos sin suministro'],
  'navigate-gestion-datos-configuracion':       ['Configuración gestión datos', 'Opciones gestión de datos'],
  'navigate-gestion-datos-metrologia':          ['Metrología', 'Configuración metrología'],
  'navigate-gestion-datos-importacion':         ['Importación datos', 'Importar datos gestión'],
  'navigate-gestion-datos-pta':                 ['PTA', 'Punto de tarifa'],
  // ── Navigation: Network status ────────────────────────────────────────────────
  'navigate-estado-red':                ['Estado de red', 'Ver estado red LoRa'],
  'navigate-estado-red-entidad':        ['Estado red entidad', 'Red de la entidad'],
  'navigate-estado-red-kpis':           ['KPIs de red', 'Indicadores de rendimiento red'],
  'navigate-estado-red-global':         ['Estado red global', 'Red global'],
  'navigate-estado-red-resumen-global': ['Resumen global red', 'Resumen estado red'],
  // ── Navigation: Additional sections ──────────────────────────────────────────
  'navigate-entidades':                 ['Entidades', 'Listado de entidades'],
  'navigate-cups':                      ['CUPS', 'Puntos de suministro'],
  'navigate-agrupaciones':              ['Listado de agrupaciones', 'Ver todas las agrupaciones'],
  'navigate-sensorica':                 ['Sensores', 'Sensorica fugas'],
  'navigate-sensorica-presion':         ['Sensores de presión', 'Presión sensorica'],
  'navigate-sensorica-caudal':          ['Sensores de caudal', 'Medición caudal'],
  'navigate-ordenes':                   ['Órdenes de trabajo', 'OTs'],
  'navigate-ordenes-rutas':             ['Rutas de trabajo', 'Gestión de rutas'],
  'navigate-instalador':                ['Instalador', 'Activar dispositivo'],
  'navigate-instalador-historico':      ['Histórico instalaciones', 'Instalaciones anteriores'],
  'navigate-abonado':                   ['Panel abonado', 'Mi consumo'],
  'navigate-abonado-configuracion':     ['Configuración abonado', 'Ajustes abonado'],
};

/** Computed intent display row for the intents panel. */
interface IntentRow {
  id: string;
  action: ChatAction;
  payload: string | undefined;
  responseKey: string;
  patternCount: number;
  /** Regex source strings for the expandable patterns section. */
  patterns: string[];
  /** Optional human-readable description of what the intent does. */
  description: string | undefined;
  /** Usage examples shown as guide chips. */
  examples: string[];
  /** Pre-computed human-readable label for this intent. */
  humanLabel: string;
  /** i18n key for the action type badge. */
  actionKey: string;
}

// ANCHOR: ChatbotInstructionsComponent — read-only intent reference panel
/**
 * Read-only dialog listing the chatbot's configured intents, with filter, patterns and example phrases.
 * Emits `exampleClicked` with the example text, which the parent sends as a message, and `closed` on dismiss.
 */
@Component({
  selector: 'app-chatbot-instructions',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chatbot-train.component.html',
  styleUrl: './chatbot-train.component.scss',
  imports: [TranslateModule],
})
export class ChatbotInstructionsComponent {
  /** Emitted when the user closes the dialog. */
  readonly closed = output<void>();
  /** Emitted when the user clicks an example chip — carries the example text to send. */
  readonly exampleClicked = output<string>();

  private readonly dialogRef = viewChild<ElementRef<HTMLDialogElement>>('dialogEl');

  // Expose enum to template.
  protected readonly ChatAction = ChatAction;

  // ANCHOR: State signals
  protected readonly intentFilter = signal<string>('');

  /** Set of intent IDs whose pattern list is currently expanded. */
  protected readonly expandedPatternIds = signal<ReadonlySet<string>>(new Set());

  /** Intent rows filtered by id, payload, description, label, or example text. */
  protected readonly filteredIntents = computed<IntentRow[]>(() => {
    const q = this.intentFilter().toLowerCase().trim();
    return CHATBOT_INTENTS
      .map(i => ({
        id:           i.id,
        action:       i.action,
        payload:      i.payload,
        responseKey:  i.response,
        patternCount: i.patterns.length,
        patterns:     i.patterns.map(p => p.source),
        description:  i.description,
        examples:     INTENT_EXAMPLES[i.id] ?? [],
        humanLabel:   this.humanLabel(i.id),
        actionKey:    this.actionLabelKey(i.action),
      }))
      .filter(r => !q
        || r.id.includes(q)
        || (r.payload ?? '').includes(q)
        || (r.description ?? '').toLowerCase().includes(q)
        || r.examples.some(e => e.toLowerCase().includes(q))
        || (INTENT_LABELS[r.id] ?? '').toLowerCase().includes(q)
      );
  });

  // Why effect(): showModal() is an imperative DOM API — not a derived value.
  private readonly _openModal = effect(() => {
    const el = this.dialogRef()?.nativeElement;
    if (el && !el.open) { el.showModal(); }
  });

  // ANCHOR: Handlers

  protected close(): void {
    this.dialogRef()?.nativeElement?.close();
    this.closed.emit();
  }

  protected onDialogClick(event: MouseEvent, dialogEl: HTMLDialogElement): void {
    if (event.target === dialogEl) {
      dialogEl.close();
      this.closed.emit();
    }
  }

  protected onCancel(event: Event): void {
    event.preventDefault();
    this.dialogRef()?.nativeElement?.close();
    this.closed.emit();
  }

  // Closes the dialog and emits the example text so the parent can send it.
  protected onExampleClick(example: string): void {
    this.dialogRef()?.nativeElement?.close();
    this.exampleClicked.emit(example);
  }

  protected getInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /** Toggle the expanded-patterns state for a given intent row. */
  protected togglePatterns(id: string): void {
    this.expandedPatternIds.update(current => {
      const next = new Set(current);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }

  /**
   * Returns a human-readable label for the intent id.
   * Uses the INTENT_LABELS map; falls back to auto-generating a readable string from the id.
   */
  private humanLabel(id: string): string {
    if (INTENT_LABELS[id]) { return INTENT_LABELS[id]; }
    // Auto-generate: clean up prefixes and capitalise each word.
    const parts = id.split('-');
    if (parts[0] === 'navigate') { parts.shift(); }
    else if (parts[0] === 'query') { parts[0] = 'Consulta:'; }
    else if (parts[0] === 'context') { parts[0] = 'Cambiar'; }
    return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  }

  private actionLabelKey(action: ChatAction): string {
    switch (action) {
      case ChatAction.NAVIGATE:    return 'chatbot.instructions.action-navigate';
      case ChatAction.SET_CONTEXT: return 'chatbot.instructions.action-context';
      case ChatAction.SET_DATE:    return 'chatbot.instructions.action-date';
      default:                     return 'chatbot.instructions.action-answer';
    }
  }
}